/**
 * The API's allowed-origin rules, mirrored so the builder rejects exactly what the API rejects:
 * a dotted DNS hostname (or `localhost`), optionally carrying an http(s) scheme, and never a port,
 * path, query or credentials. Punycoded IDN suffixes such as `xn--p1ai` (.рф) are hostnames too.
 */
const HOSTNAME_PATTERN = /^(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+(?:[a-z]{2,}|xn--[a-z\d-]{2,59})$/;

/**
 * The API reads the port off the raw value, because `new URL()` drops a scheme's default port and
 * would hide the `:443` in `https://example.com:443`.
 */
const hasPort = (input: string): boolean =>
  /:\d+$/.test(input.replace(/^https?:\/\//i, '').replace(/\/+$/, ''));

/**
 * The value to store for a typed domain, or null when the API would reject it.
 *
 * A bare hostname is kept bare: the API authorizes that host *and its subdomains*, which is what
 * someone entering `example.com` expects for `www.example.com`. Adding a scheme is how the API is
 * told to narrow the match to that one exact origin, so a typed scheme is preserved.
 */
export const normalizeContactCaptureOrigin = (value: string): string | null => {
  const input = value.trim();
  if (!/^(?:https?:\/\/)?[^/?#]+\/?$/i.test(input) || /[\s\\@*%]/.test(input)) return null;
  if (hasPort(input)) return null;

  const scheme = /^(https?):\/\//i.exec(input)?.[1].toLowerCase();
  let url: URL;
  try {
    url = new URL(scheme ? input : `https://${input}`);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;

  // URL validates IP literals and accepts underscores, neither of which the API treats as a host.
  const { hostname } = url;
  if (hostname !== 'localhost' && !HOSTNAME_PATTERN.test(hostname)) return null;

  return scheme ? `${scheme}://${hostname}` : hostname;
};

export const normalizeContactCaptureDomainInput = normalizeContactCaptureOrigin;

export const getContactCaptureDomainError = (value: string): string | null => {
  if (!value.trim()) return 'Enter the website domain.';
  return normalizeContactCaptureDomainInput(value)
    ? null
    : 'Enter one domain without a port or path, for example example.com. '
      + 'A domain on its own also covers its subdomains; add https:// to match that origin exactly.';
};
